import * as React from "react";

/**
 * Deterministic warm-gradient discs seeded from an id/name.
 * Apple-style: no fake photos, just crisp code-drawn avatars with
 * the person's initials.
 *
 * Usage:
 *   <EntityAvatar kind="person" name="Isla Mackenzie" id={u.id} size={32} />
 *   <EntityAvatar kind="site" name="Vine Lane" id={s.id} size={24} />
 *   <EntityAvatar kind="company" name="Harbour & Vine" id={c.id} size={40} />
 */

// A curated warm palette — restrained set of 12 gradients, all conforming
// to the app's off-white background (contrast 4.5:1+ against white).
const GRADIENTS = [
  ["#6E3CFA", "#8B5CF6"], // brand-violet range
  ["#F59E0B", "#F97316"], // amber → orange
  ["#0EA5E9", "#3B82F6"], // sky → blue
  ["#10B981", "#059669"], // emerald
  ["#EF4444", "#DC2626"], // red
  ["#EC4899", "#DB2777"], // pink
  ["#7C3AED", "#6D28D9"], // violet deep
  ["#14B8A6", "#0891B2"], // teal → cyan
  ["#F97316", "#EA580C"], // orange → burnt
  ["#84CC16", "#65A30D"], // lime → olive
  ["#8B5CF6", "#6366F1"], // violet → indigo
  ["#F43F5E", "#E11D48"], // rose
];

function hash(input) {
  const s = String(input || "");
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

function initials(name, kind) {
  const s = (name || "").trim();
  if (!s) return "?";
  if (kind === "company") {
    // Use first character (wordmark-style)
    return s.charAt(0).toUpperCase();
  }
  const parts = s.split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
}

export default function EntityAvatar({
  kind = "person",
  name = "",
  id = "",
  size = 28,
  className = "",
  title,
  testId,
  ring = false,
}) {
  const seed = hash(id || name);
  const gradient = GRADIENTS[seed % GRADIENTS.length];
  const label = initials(name, kind);
  // Rounded-square for site, disc for person + company
  const radius = kind === "site" ? Math.max(4, Math.round(size * 0.22)) : "50%";
  const fontSize = Math.round(size * 0.42);
  return (
    <div
      className={`inline-flex items-center justify-center shrink-0 select-none ${ring ? "ring-2 ring-white" : ""} ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
        color: "white",
        fontSize,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        boxShadow: "0 1px 2px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.14)",
      }}
      role="img"
      aria-label={title || `${name || "Unknown"} avatar`}
      title={title || name}
      data-testid={testId || `entity-avatar-${kind}`}
    >
      {label}
    </div>
  );
}
